import { useCallback, useEffect, useRef, useState } from 'react';
import './EventTicker.css';

const RESUME_AUTO_MS = 3000;

function EventTicker({ events, selectedEventId, onEventSelect }) {
  const containerRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const [isManual, setIsManual] = useState(false);
  const items = [...events, ...events];

  const enableManualScroll = useCallback(() => {
    setIsManual(true);
    clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      setIsManual(false);
      if (containerRef.current) {
        containerRef.current.scrollLeft = 0;
      }
    }, RESUME_AUTO_MS);
  }, []);

  useEffect(() => {
    return () => clearTimeout(resumeTimeoutRef.current);
  }, []);

  const wrapScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const halfWidth = container.scrollWidth / 2;
    if (halfWidth <= 0) return;

    if (container.scrollLeft >= halfWidth) {
      container.scrollLeft -= halfWidth;
    }
  }, []);

  const handleSelect = (event, buttonRef) => {
    enableManualScroll();
    onEventSelect(event);
    buttonRef?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  };

  const handleWheel = (event) => {
    if (!containerRef.current) return;

    enableManualScroll();
    containerRef.current.scrollLeft += event.deltaY;
    wrapScroll();
    event.preventDefault();
  };

  const handleScroll = () => {
    if (isManual) {
      wrapScroll();
      enableManualScroll();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`event-ticker${isManual ? ' event-ticker--manual' : ''}`}
      aria-label="Upcoming events"
      onWheel={handleWheel}
      onScroll={handleScroll}
      onTouchStart={enableManualScroll}
      onMouseDown={enableManualScroll}
    >
      <div className="event-ticker__track">
        {items.map((event, index) => (
          <CategoryButton
            key={`${event.id}-${index}`}
            event={event}
            isActive={selectedEventId === event.id}
            onSelect={handleSelect}
          />
        ))}
      </div>
    </div>
  );
}

function CategoryButton({ event, isActive, onSelect }) {
  const buttonRef = useRef(null);

  return (
    <button
      ref={buttonRef}
      type="button"
      className={`event-ticker__item${
        isActive ? ' event-ticker__item--active' : ''
      }`}
      onClick={() => onSelect(event, buttonRef.current)}
      aria-pressed={isActive}
    >
      {event.title}
            <span className="event-ticker__separator" aria-hidden="true">
              |
            </span>
    </button>
  );
}

export default EventTicker;
